import { z } from 'zod'
import { idSchema, isoDateTimeSchema, text } from './common.js'

/**
 * A login for one member of staff, created only by an administrator — there
 * is no self-signup. Distinct from `workers` (payroll/attendance): a worker
 * need not have a login, and a login need not be tied to a worker record.
 */

export const staffRoleSchema = z.enum(['admin', 'staff'])
export type StaffRole = z.infer<typeof staffRoleSchema>

/**
 * The parts of the app a staff account can be given access to, one per nav
 * item except Dashboard (always visible to anyone signed in — there is
 * nothing there to restrict, and an account with access to nothing at all
 * would have nowhere to land after signing in).
 *
 * An 'admin' account ignores this list entirely and always has everything —
 * see the server-side enforcement in lib/section-access.ts for exactly what
 * each key gates and the cross-cutting reads (client/car lookup from the
 * jobcard editor, catalog suggestions, the inspection checklist, settings
 * needed for currency/date formatting everywhere) that stay reachable
 * regardless of section, because blocking them breaks unrelated pages.
 */
export const staffSectionSchema = z.enum([
  'jobcards',
  'finance',
  'accounting',
  'storage',
  'suppliers',
  'clients',
  'workers',
  'calendar',
  'catalog',
  'settings',
])
export type StaffSection = z.infer<typeof staffSectionSchema>
export const ALL_STAFF_SECTIONS: StaffSection[] = staffSectionSchema.options

/** Compared and stored lower-cased, so "Mo" and "mo" are the same account. */
const usernameSchema = z
  .string()
  .trim()
  .min(3)
  .max(40)
  .regex(/^[a-z0-9._-]+$/i, 'Letters, numbers, dot, underscore and dash only')

export const staffAccountCreateSchema = z.object({
  username: usernameSchema,
  displayName: text(80),
  role: staffRoleSchema.default('staff'),
  /** Ignored for an admin account (always full access). Defaults to none —
   *  a new staff account starts with nothing checked, not everything, so an
   *  admin who forgets to set this notices immediately rather than having
   *  granted full access by omission. */
  sections: z.array(staffSectionSchema).max(ALL_STAFF_SECTIONS.length).default([]),
  readOnly: z.boolean().default(false),
})
export type StaffAccountCreate = z.infer<typeof staffAccountCreateSchema>

export const staffAccountUpdateSchema = z.object({
  displayName: text(80).optional(),
  role: staffRoleSchema.optional(),
  active: z.boolean().optional(),
  sections: z.array(staffSectionSchema).max(ALL_STAFF_SECTIONS.length).optional(),
  readOnly: z.boolean().optional(),
})
export type StaffAccountUpdate = z.infer<typeof staffAccountUpdateSchema>

/** Never carries the password hash — the client has no business seeing it. */
export const staffAccountSchema = z.object({
  id: idSchema,
  username: z.string(),
  displayName: z.string(),
  role: staffRoleSchema,
  active: z.boolean(),
  /** True right after creation or a reset, until the account sets its own. */
  mustChangePassword: z.boolean(),
  /** Meaningless for an admin account, which always has every section. */
  sections: z.array(staffSectionSchema),
  /** Meaningless for an admin account, which is never read-only. */
  readOnly: z.boolean(),
  lastLoginAt: isoDateTimeSchema.nullable(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
})
export type StaffAccount = z.infer<typeof staffAccountSchema>

/**
 * What creating an account or resetting its password returns: the account
 * plus the one-time password the admin now hands to that staff member. It is
 * never retrievable again — only a fresh reset produces another one.
 */
export interface StaffAccountWithTempPassword extends StaffAccount {
  temporaryPassword: string
}

/** Capped length on the password fields bounds the cost of hashing a hostile input. */
export const loginSchema = z.object({
  username: z.string().trim().min(1).max(40),
  password: z.string().min(1).max(200),
})
export type LoginInput = z.infer<typeof loginSchema>

/** A registered passkey as the client may see it — never the key material. */
export interface StaffPasskey {
  id: string
  deviceName: string
  createdAt: string
  lastUsedAt: string | null
}

/**
 * Body of POST /api/auth/passkeys/register/verify. `response` is the browser's
 * RegistrationResponseJSON, which @simplewebauthn/server validates itself.
 */
export const passkeyRegisterVerifySchema = z.object({
  deviceName: z.string().trim().min(1).max(60),
  response: z.record(z.unknown()),
})
export type PasskeyRegisterVerifyInput = z.infer<typeof passkeyRegisterVerifySchema>

/** Body of POST /api/auth/passkeys/login/verify (AuthenticationResponseJSON). */
export const passkeyLoginVerifySchema = z.object({
  response: z.record(z.unknown()),
})
export type PasskeyLoginVerifyInput = z.infer<typeof passkeyLoginVerifySchema>

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1).max(200),
  newPassword: z.string().min(8).max(200),
})
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>

/**
 * What `GET /api/auth/me` returns — the session's own account, nothing more.
 * The client uses `sections`/`readOnly` only to shape the UI (hide a nav
 * link, disable an "Add" button); the server enforces the real boundary
 * independently, so a tampered client can inconvenience nobody but itself.
 */
export const currentStaffAccountSchema = z.object({
  id: idSchema,
  username: z.string(),
  displayName: z.string(),
  role: staffRoleSchema,
  mustChangePassword: z.boolean(),
  sections: z.array(staffSectionSchema),
  readOnly: z.boolean(),
})
export type CurrentStaffAccount = z.infer<typeof currentStaffAccountSchema>
